// src/pages/admin/CompletedEvents/CompletedEvents.jsx

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Badge, Button, Form } from 'react-bootstrap';
import { 
  FaSearch, 
  FaCalendarAlt, 
  FaEye, 
  FaStar, 
  FaDownload,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import axios from 'axios';
import '../../../../assets/css/admin/dashboard/CompletedEvents.css';
import AdminLayout from '../../../../layouts/admin/AdminLayout';

const CompletedEvents = () => {
  // State Management
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch Completed Events from API
  useEffect(() => {
    fetchCompletedEvents();
  }, []);

  const fetchCompletedEvents = async () => {
    try {
      setLoading(true);
      // Replace with your actual API endpoint
      const response = await axios.get('/api/admin/events/completed');
      setEvents(response.data);
      setFilteredEvents(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching events:', error);
      setLoading(false);
      
      // Demo Data for Development
      const demoData = [
        {
          id: 1,
          eventName: 'Grand Wedding Ceremony',
          eventDate: '2024-01-15',
          venue: 'Pearl Continental Hotel',
          customer: 'Ahmed Ali',
          totalCost: 500000,
          feedbackStatus: 'submitted',
          rating: 5
        },
        {
          id: 2,
          eventName: 'Mehndi Function',
          eventDate: '2024-01-10',
          venue: 'Royal Marquee',
          customer: 'Fatima Khan',
          totalCost: 250000,
          feedbackStatus: 'submitted',
          rating: 4
        },
        {
          id: 3,
          eventName: 'Engagement Party',
          eventDate: '2024-01-05',
          venue: 'Garden Palace',
          customer: 'Sara Ahmed',
          totalCost: 150000,
          feedbackStatus: 'pending',
          rating: 0
        },
        {
          id: 4,
          eventName: 'Barat Ceremony',
          eventDate: '2023-12-28',
          venue: 'Marquee Hall',
          customer: 'Hassan Raza',
          totalCost: 450000,
          feedbackStatus: 'submitted',
          rating: 5
        },
        {
          id: 5,
          eventName: 'Walima Reception',
          eventDate: '2023-12-20',
          venue: 'Grand Ballroom',
          customer: 'Ayesha Malik',
          totalCost: 380000,
          feedbackStatus: 'submitted',
          rating: 4
        }
      ];
      
      setEvents(demoData);
      setFilteredEvents(demoData);
    }
  };

  // Search Handler
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    filterEvents(value, filterDate);
  };

  // Date Filter Handler
  const handleDateFilter = (e) => {
    const value = e.target.value;
    setFilterDate(value);
    filterEvents(searchTerm, value);
  };

  // Filter Events Function
  const filterEvents = (search, date) => {
    let filtered = events;

    // Search Filter
    if (search) {
      filtered = filtered.filter(event => 
        event.eventName.toLowerCase().includes(search) ||
        event.customer.toLowerCase().includes(search) ||
        event.venue.toLowerCase().includes(search)
      );
    }

    // Date Filter
    if (date) {
      filtered = filtered.filter(event => event.eventDate === date);
    }

    setFilteredEvents(filtered);
  };

  // View Event Details
  const handleViewDetails = (event) => {
    Swal.fire({
      title: event.eventName,
      html: `
        <div style="text-align: left; padding: 20px;">
          <p><strong>Event Date:</strong> ${event.eventDate}</p>
          <p><strong>Venue:</strong> ${event.venue}</p>
          <p><strong>Customer:</strong> ${event.customer}</p>
          <p><strong>Total Cost:</strong> Rs. ${event.totalCost.toLocaleString()}</p>
          <p><strong>Feedback Status:</strong> ${event.feedbackStatus}</p>
          ${event.rating > 0 ? `<p><strong>Rating:</strong> ${'⭐'.repeat(event.rating)}</p>` : ''}
        </div>
      `,
      icon: 'info',
      confirmButtonColor: '#7C6FDC'
    });
  };

  // Download Report
  const handleDownloadReport = (event) => {
    Swal.fire({
      title: 'Download Report',
      text: `Downloading report for ${event.eventName}...`,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  };

  // View Feedback
  const handleViewFeedback = (event) => {
    if (event.feedbackStatus === 'pending') {
      Swal.fire({
        title: 'No Feedback Yet',
        text: 'Customer has not submitted feedback for this event.',
        icon: 'info',
        confirmButtonColor: '#7C6FDC'
      });
    } else {
      Swal.fire({
        title: 'Customer Feedback',
        html: `
          <div style="text-align: left; padding: 20px;">
            <p><strong>Rating:</strong> ${'⭐'.repeat(event.rating)}</p>
            <p><strong>Comment:</strong> "The event was amazing! Everything was perfect."</p>
            <p><strong>Submitted On:</strong> ${event.eventDate}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#7C6FDC'
      });
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterDate('');
    setFilteredEvents(events);
  };

  return (
    <AdminLayout>
      <div className="completed-events-page-cev">
        
        {/* Page Header */}
        <div className="page-header-cev">
          <h1 className="page-title-cev">
            <FaCheckCircle className="title-icon-cev" />
            Completed Events
          </h1>
          <p className="page-subtitle-cev">
            Manage and review all completed wedding events
          </p>
        </div>

        {/* Summary Cards - MOVED HERE AFTER TITLE */}
        <Row className="summary-cards-cev">
          <Col md={4}>
            <div className="summary-card-cev card-total-cev">
              <div className="card-icon-cev">
                <FaCheckCircle />
              </div>
              <div className="card-content-cev">
                <h3>{events.length}</h3>
                <p>Total Completed Events</p>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="summary-card-cev card-feedback-cev">
              <div className="card-icon-cev">
                <FaStar />
              </div>
              <div className="card-content-cev">
                <h3>{events.filter(e => e.feedbackStatus === 'submitted').length}</h3>
                <p>Feedback Received</p>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="summary-card-cev card-revenue-cev">
              <div className="card-icon-cev">
                <FaDownload />
              </div>
              <div className="card-content-cev">
                <h3>Rs. {events.reduce((sum, e) => sum + e.totalCost, 0).toLocaleString()}</h3>
                <p>Total Revenue</p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filters Section */}
        <div className="filters-section-cev">
          <Row className="g-3">
            
            {/* Search Bar */}
            <Col md={6} lg={5}>
              <div className="search-box-cev">
                <FaSearch className="search-icon-cev" />
                <Form.Control
                  type="text"
                  placeholder="Search by event name, customer, or venue..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="search-input-cev"
                />
              </div>
            </Col>

            {/* Date Filter */}
            <Col md={4} lg={3}>
              <div className="date-filter-box-cev">
                <FaCalendarAlt className="calendar-icon-cev" />
                <Form.Control
                  type="date"
                  value={filterDate}
                  onChange={handleDateFilter}
                  className="date-input-cev"
                />
              </div>
            </Col>

            {/* Reset Button */}
            <Col md={2} lg={2}>
              <Button 
                variant="outline-secondary" 
                className="reset-btn-cev"
                onClick={handleResetFilters}
              >
                <FaFilter /> Reset
              </Button>
            </Col>

            {/* Results Count */}
            <Col lg={2} className="text-end">
              <div className="results-count-cev">
                <span className="count-number-cev">{filteredEvents.length}</span>
                <span className="count-label-cev">Events Found</span>
              </div>
            </Col>

          </Row>
        </div>

        {/* Events Table */}
        <div className="events-table-container-cev">
          
          {loading ? (
            <div className="loading-state-cev">
              <div className="spinner-cev"></div>
              <p>Loading completed events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="empty-state-cev">
              <FaTimesCircle className="empty-icon-cev" />
              <h3>No Completed Events Found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="table-responsive-cev">
              <Table className="events-table-cev" hover>
                <thead className="table-head-cev">
                  <tr>
                    <th className="table-header-cev">#</th>
                    <th className="table-header-cev">Event Name</th>
                    <th className="table-header-cev">Event Date</th>
                    <th className="table-header-cev">Venue</th>
                    <th className="table-header-cev">Customer</th>
                    <th className="table-header-cev">Total Cost</th>
                    <th className="table-header-cev">Feedback</th>
                    <th className="table-header-cev text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body-cev">
                  {filteredEvents.map((event, index) => (
                    <tr key={event.id} className="table-row-cev">
                      <td className="table-cell-cev">{index + 1}</td>
                      <td className="table-cell-cev">
                        <div className="event-name-cell-cev">
                          <span className="event-name-cev">{event.eventName}</span>
                        </div>
                      </td>
                      <td className="table-cell-cev">
                        <span className="date-badge-cev">
                          <FaCalendarAlt className="me-1" />
                          {new Date(event.eventDate).toLocaleDateString('en-GB')}
                        </span>
                      </td>
                      <td className="table-cell-cev">{event.venue}</td>
                      <td className="table-cell-cev">
                        <span className="customer-name-cev">{event.customer}</span>
                      </td>
                      <td className="table-cell-cev">
                        <span className="cost-amount-cev">
                          Rs. {event.totalCost.toLocaleString()}
                        </span>
                      </td>
                      <td className="table-cell-cev">
                        {event.feedbackStatus === 'submitted' ? (
                          <Badge bg="success" className="feedback-badge-cev">
                            <FaStar /> Submitted ({event.rating}★)
                          </Badge>
                        ) : (
                          <Badge bg="warning" className="feedback-badge-cev">
                            Pending
                          </Badge>
                        )}
                      </td>
                      <td className="table-cell-cev text-center">
                        <div className="action-buttons-cev">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="action-btn-cev view-btn-cev"
                            onClick={() => handleViewDetails(event)}
                            title="View Details"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="action-btn-cev feedback-btn-cev"
                            onClick={() => handleViewFeedback(event)}
                            title="View Feedback"
                          >
                            <FaStar />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="action-btn-cev download-btn-cev"
                            onClick={() => handleDownloadReport(event)}
                            title="Download Report"
                          >
                            <FaDownload />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

        </div>

      </div>
    </AdminLayout>
  );
};

export default CompletedEvents;